import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, CheckIcon, ChevronRightIcon, MapPinIcon, ShieldAlertIcon, SparklesIcon } from 'lucide-react';
import { alertPlaces, avisQuestions, channelPrefs, topicPrefs } from '../data/avis';
function Toggle({
  label,
  defaultOn
}: {
  label: string;
  defaultOn: boolean;
}) {
  const [on, setOn] = useState(defaultOn);
  return <button type="button" role="switch" aria-checked={on} aria-label={label} onClick={() => setOn(v => !v)} className={`relative h-6 w-11 shrink-0 rounded-full transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2 ${on ? 'bg-ville-indigo' : 'bg-slate-300'}`} data-id="element-2118" data-mp="33ijoz2jigspg">
      <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-[left] duration-150 ease-ville ${on ? 'left-[22px]' : 'left-0.5'}`} data-id="element-2119" data-mp="spp2srsegd34" />
    </button>;
}
export function ParametresAvis() {
  const [question, setQuestion] = useState('');
  return <main className="bg-white" data-id="element-2120" data-mp="3zu5sa5cobpi">
      <div className="mx-auto max-w-[860px] px-5 py-12 lg:px-10 lg:py-16" data-id="element-2121" data-mp="3yswaa3zfx9w">
        <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1.5 text-sm text-slate-500" data-id="element-2122" data-mp="hqz498wzexy9">
          <Link to="/" className="font-semibold text-ville-deep hover:text-ville-indigo" data-id="element-2123" data-mp="csxigg868kj3">
            Home
          </Link>
          <ChevronRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-2124" data-mp="3l7pmcm5u91gb" />
          <Link to="/notifications" className="font-semibold text-ville-deep hover:text-ville-indigo" data-id="element-2125" data-mp="3h6vyfssebmnf">
            Notifications
          </Link>
          <ChevronRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-2126" data-mp="3v395s937u7o6" />
          <span aria-current="page" data-id="element-2127" data-mp="3q4xvxycx7x5y">Settings</span>
        </nav>

        <header className="mt-4" data-id="element-2128" data-mp="1aev42mhul5z9">
          <h1 className="text-4xl font-extrabold tracking-tight text-ville-navy lg:text-5xl" data-id="element-2129" data-mp="1adtkftmrnb5x">
            Notification preferences
          </h1>
          <p className="mt-2 max-w-2xl text-[17px] text-slate-600" data-id="element-2130" data-mp="1dh9fkonlxtei">
            Choose the information you want to receive, and how the Town can reach you.
          </p>
        </header>

        <section aria-labelledby="channels-title" className="mt-10" data-id="element-2131" data-mp="1hkfgdgryasdl">
          <h2 id="channels-title" className="text-xl font-extrabold tracking-tight text-ville-navy" data-id="element-2132" data-mp="1mih1e78xt1rp">
            How would you like to be notified?
          </h2>
          <ul className="mt-4 space-y-2" data-id="element-2133" data-mp="ttryc2obmmdb">
            {channelPrefs.map(channel => <li key={channel.id} className="flex items-center gap-4 rounded-2xl border border-slate-200 px-5 py-4" data-id="element-2134" data-mp="yrk15rqi7swa">
                <span className="min-w-0 flex-1" data-id="element-2135" data-mp="13q507nu0s6j5">
                  <span className="block font-bold text-ville-navy" data-id="element-2136" data-mp="13pd3g9oos2ra">{channel.label}</span>
                  <span className="block text-sm text-slate-500" data-id="element-2137" data-mp="18jzeucurpvbd">{channel.destination}</span>
                </span>
                <Toggle label={channel.label} defaultOn={channel.enabled} data-id="element-2138" data-mp="g1lrj7725rqj" />
              </li>)}
          </ul>
          <Link to="/my-profile" className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-ville-indigo hover:underline" data-id="element-2139" data-mp="8im9p5z8w89a">
            Change my contact details
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-2140" data-mp="3uohqrrpgx39o" />
          </Link>
        </section>

        <section aria-labelledby="essential-title" className="mt-10 rounded-2xl border border-slate-200 bg-slate-50 p-5" data-id="element-2141" data-mp="3pp6aejt0jzsm">
          <h2 id="essential-title" className="flex items-center gap-2 text-[15px] font-extrabold text-ville-navy" data-id="element-2142" data-mp="3lqfnt6m6c0jr">
            <ShieldAlertIcon className="h-5 w-5 text-red-600" aria-hidden="true" data-id="element-2143" data-mp="3byjm7lkvpsas" />
            Safety and emergency alerts
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-slate-600" data-id="element-2144" data-mp="3711bbpnax305">
            These messages may be sent when they are necessary for your safety or the safety of the community. They cannot be turned off completely.
          </p>
          <p className="mt-3 inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-xs font-bold text-slate-600" data-id="element-2145" data-mp="2y14rq2frquby">
            <CheckIcon className="h-4 w-4 text-ville-indigo" aria-hidden="true" data-id="element-2146" data-mp="3rohmq5sah0p7" />
            Always on
          </p>
        </section>

        <section aria-labelledby="interests-title" className="mt-10" data-id="element-2147" data-mp="3dwkywdxnlcym">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500" data-id="element-2148" data-mp="3hyfxvbs43e98">My preferences</p>
          <h2 id="interests-title" className="mt-1 text-xl font-extrabold tracking-tight text-ville-navy" data-id="element-2149" data-mp="33ynb1rjphi6j">
            What interests you
          </h2>
          <ul className="mt-4 space-y-2" data-id="element-2150" data-mp="38zkq3v9f5w3t">
            {topicPrefs.map(topic => <li key={topic.id} className="flex items-center gap-4 rounded-2xl border border-slate-200 px-5 py-4" data-id="element-2151" data-mp="2v02zrbgh6asc">
                <span className="min-w-0 flex-1" data-id="element-2152" data-mp="2z26dy7uj3thb">
                  <span className="block font-bold text-ville-navy" data-id="element-2153" data-mp="t6vme7kfa393">{topic.label}</span>
                  <span className="block text-sm text-slate-500" data-id="element-2154" data-mp="x98omlzynj14">{topic.description}</span>
                </span>
                <Toggle label={topic.label} defaultOn={topic.enabled} data-id="element-2155" data-mp="2hpzp6hcbulxv" />
              </li>)}
          </ul>
        </section>

        <section aria-labelledby="places-title" className="mt-10" data-id="element-2156" data-mp="2dr0ppc9d7hdt">
          <h2 id="places-title" className="text-xl font-extrabold tracking-tight text-ville-navy" data-id="element-2157" data-mp="2rhm9r4ak1xda">
            My places
          </h2>
          <p className="mt-1 text-[15px] text-slate-600" data-id="element-2158" data-mp="2mnrhvdksqc34">
            Your places let the Town send you the notices that concern you.
          </p>
          <ul className="mt-4 space-y-2" data-id="element-2159" data-mp="2wggrs40p7hwc">
            {alertPlaces.map(place => <li key={place.id} className="flex items-center gap-4 rounded-2xl border border-slate-200 px-5 py-4" data-id="element-2160" data-mp="3af6sruqn21pz">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-ville-indigo-soft text-ville-indigo" aria-hidden="true" data-id="element-2161" data-mp="35g3viwgwgzsk">
                  <MapPinIcon className="h-5 w-5" data-id="element-2162" data-mp="1g3i9xhhadf0b" />
                </span>
                <span className="min-w-0 flex-1" data-id="element-2163" data-mp="1b3vt6y291gmr">
                  <span className="block font-bold text-ville-navy" data-id="element-2164" data-mp="3b4u24tn4ipia">{place.address}</span>
                  <span className="block text-sm text-slate-500" data-id="element-2165" data-mp="3g3nicttsoum5">{place.label}</span>
                </span>
                <Toggle label={`Alerts for ${place.address}`} defaultOn data-id="element-2166" data-mp="3l300505t7bhv" />
              </li>)}
          </ul>
          <Link to="/my-profile" className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-ville-indigo hover:underline" data-id="element-2167" data-mp="3p4ll78shyw35">
            Manage my addresses
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-2168" data-mp="2w3k3ny75t3n" />
          </Link>
        </section>

        <section aria-labelledby="atna-settings-title" className="mt-10 rounded-3xl border border-slate-200 p-5" data-id="element-2169" data-mp="7owqeg9bd9hu">
          <h2 id="atna-settings-title" className="flex items-center gap-2 text-[15px] font-extrabold text-ville-navy" data-id="element-2170" data-mp="cmf0i9z2hurf">
            <SparklesIcon className="h-5 w-5 text-ville-indigo" aria-hidden="true" data-id="element-2171" data-mp="gmgs1qb397sz" />
            A question about your notices?
          </h2>
          <p className="mt-1.5 text-sm leading-relaxed text-slate-600" data-id="element-2172" data-mp="2666xfeahzaen">
            ATNA can explain why you received a message, or how to change your preferences.
          </p>

          <form onSubmit={event => event.preventDefault()} className="mt-4 flex items-center gap-2 rounded-full border border-slate-200 py-1.5 pl-4 pr-1.5 focus-within:border-ville-indigo" data-id="element-2173" data-mp="1x4zbzwt2y41z">
            <label htmlFor="atna-settings" className="sr-only" data-id="element-2174" data-mp="2owetxdjoz2lv">
              Ask your question
            </label>
            <input id="atna-settings" type="text" value={question} onChange={event => setQuestion(event.target.value)} placeholder="Ask ATNA…" className="min-w-0 flex-1 border-0 bg-transparent py-2 text-sm text-ville-navy placeholder:text-slate-400 focus:outline-none" data-id="element-2175" data-mp="2kvuxydauu51b" />
            <button type="submit" className="rounded-full bg-ville-indigo px-4 py-2 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2" data-id="element-2176" data-mp="33sw4w3nyyf7w">
              Ask
            </button>
          </form>

          <ul className="mt-3 flex flex-wrap gap-2" data-id="element-2177" data-mp="276rkqmqf3yls">
            {avisQuestions.map(item => <li key={item} data-id="element-2178" data-mp="2uwci4anbiyel">
                <button type="button" onClick={() => setQuestion(item)} className="rounded-full bg-slate-50 px-3.5 py-1.5 text-sm text-slate-600 transition-colors duration-150 ease-ville hover:bg-ville-indigo-soft hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-2179" data-mp="2uw2p0e6z8b4o">
                  {item}
                </button>
              </li>)}
          </ul>
        </section>
      </div>
    </main>;
}